import { LoadSpinner } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import React from 'react'
import { useLocation } from 'react-router-dom'

import { Card, LineItem, Tag } from '@/components'
import { useAlertContext } from '@/context'
import { useReservationHome } from '@/hooks'

import { leaseMapData } from './HomeView.data'

type Props = {
  homeId: number
}

export const HomeDetail = ({ homeId }: Props) => {
  const { isLoading, data } = useReservationHome({ homeId })

  const { pathname } = useLocation()
  const isHomeView = pathname.includes('homes')

  const ADMIN_URL = process.env.ADMIN_URL

  const homeDetails = data?.home
  const {
    baths = '',
    bedrooms = '',
    floor,
    leases,
    mailBoxNumber,
    hasBalcony,
    airConditioningAvailability,
    inUnitLaundry,
    propertyOnlinePortal,
    propertyPortalPassword,
    propertyPortalUsername,
    unitContactPhone,
    unitContactAddress,
    unitContactEmail,
    uninstallDate,
  } = homeDetails || {}
  const { setAlert } = useAlertContext()

  const handleClick = (message: string, value?: string) => {
    if (message.length > 0 && value) {
      navigator.clipboard.writeText(value)
      setAlert(message, 'success')
    }
  }

  const lease = leases && leases.length > 0 ? leases[0] : undefined

  return (
    <>
      {isLoading || !homeDetails ? (
        <div className="flex h-120 w-full items-center justify-center">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <div>
          <div className="mb-2 flex">
            {homeDetails?.blocked && (
              <Tag className="mr-4" variant={'error'}>
                Blocked
              </Tag>
            )}
            {homeDetails?.property?.landingLite && (
              <Tag className="mr-4" variant="success">
                {' '}
                Lite{' '}
              </Tag>
            )}
            {homeDetails?.occupied && <Tag variant="success"> Occupied </Tag>}
          </div>
          <ContainerStyled>
            <Card cardClassName="mb-4" headerText="Home Details" minimizable={true}>
              <div className="flex w-full flex-col px-4">
                <LineItem
                  label="Property"
                  link={{
                    href: `/properties/${homeDetails?.property?.id}`,
                    target: '_self',
                  }}
                  value={homeDetails?.property?.name}
                />
                <LineItem label="Unit Number" value={homeDetails.unitNumber} />
                <LineItem booleanCheck={true} label="Direct" value={homeDetails?.property?.landingDirect} />
                <LineItem emptyCheck={true} label="Unit Contact" value={unitContactPhone} />
                <LineItem emptyCheck={true} label="Unit Email" value={unitContactEmail} />
                <LineItem emptyCheck={true} label="Unit Address" value={unitContactAddress} />
                <LineItem emptyCheck={true} label="Uninstall Date" value={uninstallDate} />
                <LineItem
                  label="Admin Link"
                  link={{ href: `${ADMIN_URL}/admin/homes/${homeDetails.slug}`, target: '_blank' }}
                  value="Link"
                />
                {!isHomeView && (
                  <LineItem
                    label="Home Link"
                    link={{ href: `/homes/${homeDetails.id}`, target: '_self' }}
                    value="Link"
                  />
                )}
              </div>
            </Card>
            <Card cardClassName="mb-4" headerText="Home Information" minimizable={true}>
              <div className="flex w-full flex-col px-4">
                <LineItem label="Bed Bath" value={`${bedrooms} - ${baths}`} />
                <LineItem label="Floor" value={floor} />
                <LineItem label="Mail Box" value={mailBoxNumber} />
                <LineItem label="Blocked" value={homeDetails?.blocked} />
                <LineItem label="Elevator" value={homeDetails?.property?.elevator} />
                <LineItem label="Balcony" value={hasBalcony} />
                <LineItem label="AC" value={airConditioningAvailability && airConditioningAvailability > 0} />
                <LineItem label="In Unit W/D" value={inUnitLaundry} />
              </div>
            </Card>

            {lease && (
              <Card cardClassName="mb-4" headerText="Lease Information" minimizable={true}>
                <div className="flex w-full flex-col px-4">
                  {leaseMapData.map(({ field, ...lineProps }, index) => {
                    return (
                      <LineItem
                        last={index === leaseMapData.length - 1}
                        value={lease[field]}
                        {...lineProps}
                        key={index}
                      />
                    )
                  })}
                </div>
              </Card>
            )}

            {propertyOnlinePortal && (
              <Card cardClassName="mb-4" headerText="Property Portal" minimizable={true}>
                <div className="flex w-full flex-col px-4">
                  <button onDoubleClick={() => handleClick('Username copied!', propertyPortalUsername)}>
                    <LineItem label="Username" value={propertyPortalUsername} />
                  </button>
                  <button onDoubleClick={() => handleClick('Password copied!', propertyPortalPassword)}>
                    <LineItem label="Password" last={true} value={propertyPortalPassword} />
                  </button>
                  <a
                    className=" mb-2 w-full rounded-lg bg-action-primary py-2 text-center font-medium text-bg-primary"
                    href={propertyOnlinePortal}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Property Portal Link
                  </a>
                </div>
              </Card>
            )}
          </ContainerStyled>
        </div>
      )}
    </>
  )
}

export const ContainerStyled = styled.div`
  padding: 1rem 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 1rem;
  }
`
